import React, { CSSProperties, useEffect, useState } from 'react'
import type { User } from '../../types'

interface AvatarProps {
  user: User | { fullName: string; avatarUrl?: string }
  size?: 'sm' | 'md' | 'lg'
  style?: CSSProperties
}

function generateColor(name: string): string {
  const colors = [
    '#6366f1', '#8b5cf6', '#ec4899', '#ef4444',
    '#f97316', '#eab308', '#22c55e', '#14b8a6',
    '#3b82f6', '#06b6d4',
  ]
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

const sizeMap = { sm: 24, md: 32, lg: 40 }
const fontSizeMap = { sm: 10, md: 13, lg: 16 }

export const Avatar: React.FC<AvatarProps> = ({ user, size = 'md', style }) => {
  const px = sizeMap[size]
  const fs = fontSizeMap[size]
  const [imageFailed, setImageFailed] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  useEffect(() => {
    setImageFailed(false)
    setImageLoaded(false)
  }, [user.avatarUrl])

  const initials = user.fullName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || '?'

  if (user.avatarUrl && !imageFailed) {
    return (
      <div
        style={{
          width: px,
          height: px,
          borderRadius: '50%',
          background: generateColor(user.fullName),
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: fs,
          fontWeight: 600,
          flexShrink: 0,
          overflow: 'hidden',
          position: 'relative',
          ...style,
        }}
      >
        {initials}
        <img
          src={user.avatarUrl}
          alt={user.fullName}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageFailed(true)}
          style={{
            width: px,
            height: px,
            borderRadius: '50%',
            objectFit: 'cover',
            flexShrink: 0,
            position: 'absolute',
            inset: 0,
            opacity: imageLoaded ? 1 : 0,
            transition: 'opacity 0.15s ease',
          }}
        />
      </div>
    )
  }

  return (
    <div
      title={user.fullName}
      style={{
        width: px,
        height: px,
        borderRadius: '50%',
        background: generateColor(user.fullName),
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: fs,
        fontWeight: 600,
        flexShrink: 0,
        userSelect: 'none',
        ...style,
      }}
    >
      {initials}
    </div>
  )
}
